import { OnboardDeviceOnlinePeer, OnboardDevicesOnlinePeersResponse } from '../interfaces';

export interface MergedOnboardDeviceOnlinePeerRow {
    id: string;
    main: OnboardDeviceOnlinePeer | null;
    secondary: OnboardDeviceOnlinePeer | null;
}

export const emptyOnboardDevicesOnlinePeers: OnboardDevicesOnlinePeersResponse = {
    vpn_server_main: [],
    vpn_server_secondary: [],
};

const EMPTY_VALUE = '-';
const KYIV_DATETIME_FORMATTER = new Intl.DateTimeFormat('uk-UA', {
    timeZone: 'Europe/Kyiv',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
});

const getPeerStableKey = (peer: OnboardDeviceOnlinePeer): string => {
    if (peer.deviceId) {
        return `deviceId:${peer.deviceId}`;
    }

    if (peer.pubkey) {
        return `pubkey:${peer.pubkey}`;
    }

    if (peer.name) {
        return `name:${peer.name}`;
    }

    if (peer.endpoint) {
        return `endpoint:${peer.endpoint}`;
    }

    return 'unknown';
};

const getUniqueRowKey = (
    rowsByKey: Map<string, MergedOnboardDeviceOnlinePeerRow>,
    peer: OnboardDeviceOnlinePeer,
    serverName: 'main' | 'secondary',
    index: number,
): string => {
    const baseKey = getPeerStableKey(peer);
    const fallbackKey = `${serverName}:${index}`;
    const key = baseKey === 'unknown' ? fallbackKey : baseKey;
    const existing = rowsByKey.get(key);

    if (!existing) {
        return key;
    }

    if (serverName === 'main' && !existing.main) {
        return key;
    }

    if (serverName === 'secondary' && !existing.secondary) {
        return key;
    }

    return `${key}#${serverName}-${index}`;
};

export const mergeOnboardDevicesOnlinePeers = (
    mainPeers: OnboardDeviceOnlinePeer[],
    secondaryPeers: OnboardDeviceOnlinePeer[],
): MergedOnboardDeviceOnlinePeerRow[] => {
    const rowsByKey = new Map<string, MergedOnboardDeviceOnlinePeerRow>();

    mainPeers.forEach((peer, index) => {
        const key = getUniqueRowKey(rowsByKey, peer, 'main', index);
        const existing = rowsByKey.get(key);

        if (existing) {
            existing.main = peer;
            return;
        }

        rowsByKey.set(key, {
            id: key,
            main: peer,
            secondary: null,
        });
    });

    secondaryPeers.forEach((peer, index) => {
        const key = getUniqueRowKey(rowsByKey, peer, 'secondary', index);
        const existing = rowsByKey.get(key);

        if (existing) {
            existing.secondary = peer;
            return;
        }

        rowsByKey.set(key, {
            id: key,
            main: null,
            secondary: peer,
        });
    });

    return Array.from(rowsByKey.values());
};

export const formatOnboardDevicesOnlineText = (value?: string | null): string => {
    return value || EMPTY_VALUE;
};

export const formatOnboardDevicesOnlineBoolean = (value?: boolean): string => {
    if (value === undefined || value === null) {
        return EMPTY_VALUE;
    }

    return value ? 'True' : 'False';
};

export const formatOnboardDevicesAllowedIps = (value?: string[]): string => {
    if (!Array.isArray(value) || value.length === 0) {
        return EMPTY_VALUE;
    }

    return value.join(', ');
};

export const formatOnboardDevicesOnlineSinceKyiv = (value?: string | null): string => {
    if (!value) {
        return EMPTY_VALUE;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return EMPTY_VALUE;
    }

    return KYIV_DATETIME_FORMATTER.format(date);
};

export const formatOnboardDevicesOnlineTime = (value?: number): string => {
    if (value === undefined || value === null || value < 0) {
        return EMPTY_VALUE;
    }

    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const formatOnboardDevicesOnlineBytes = (value?: number): string => {
    if (value === undefined || value === null || value < 0) {
        return EMPTY_VALUE;
    }

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let amount = value;
    let unitIndex = 0;

    while (amount >= 1024 && unitIndex < units.length - 1) {
        amount /= 1024;
        unitIndex += 1;
    }

    return `${amount.toFixed(2)} ${units[unitIndex]}`;
};

export const formatOnboardDevicesTraffic = (rawBytesValue?: number, humanValue?: string): string => {
    const formattedRawBytes = formatOnboardDevicesOnlineBytes(rawBytesValue);

    if (formattedRawBytes !== EMPTY_VALUE) {
        return formattedRawBytes;
    }

    return formatOnboardDevicesOnlineText(humanValue);
};

export const buildOnboardDevicesOnlineRows = (peers: OnboardDevicesOnlinePeersResponse) => {
    return mergeOnboardDevicesOnlinePeers(peers.vpn_server_main, peers.vpn_server_secondary).map((item) => {
        return {
            id: item.id,
            deviceId: formatOnboardDevicesOnlineText(item.main?.deviceId || item.secondary?.deviceId),
            name: formatOnboardDevicesOnlineText(item.main?.name || item.secondary?.name),

            mainOnlineSince: formatOnboardDevicesOnlineSinceKyiv(item.main?.online_since),
            mainOnlineTime: formatOnboardDevicesOnlineTime(item.main?.online_secs),
            mainLastHandshake: formatOnboardDevicesOnlineText(item.main?.human?.last_handshake),
            mainRx: formatOnboardDevicesTraffic(item.main?.rx_bytes, item.main?.human?.rx),
            mainTx: formatOnboardDevicesTraffic(item.main?.tx_bytes, item.main?.human?.tx),
            mainTotal: formatOnboardDevicesTraffic(
                item.main?.total_transfer,
                item.main?.human?.total_transfer,
            ),
            mainAllowedIps: formatOnboardDevicesAllowedIps(item.main?.allowed_ips),
            mainIsOnline: formatOnboardDevicesOnlineBoolean(item.main?.is_online),

            secondaryOnlineSince: formatOnboardDevicesOnlineSinceKyiv(item.secondary?.online_since),
            secondaryOnlineTime: formatOnboardDevicesOnlineTime(item.secondary?.online_secs),
            secondaryLastHandshake: formatOnboardDevicesOnlineText(item.secondary?.human?.last_handshake),
            secondaryRx: formatOnboardDevicesTraffic(item.secondary?.rx_bytes, item.secondary?.human?.rx),
            secondaryTx: formatOnboardDevicesTraffic(item.secondary?.tx_bytes, item.secondary?.human?.tx),
            secondaryTotal: formatOnboardDevicesTraffic(
                item.secondary?.total_transfer,
                item.secondary?.human?.total_transfer,
            ),
            secondaryAllowedIps: formatOnboardDevicesAllowedIps(item.secondary?.allowed_ips),
            secondaryIsOnline: formatOnboardDevicesOnlineBoolean(item.secondary?.is_online),
        };
    });
};
